import { Loader } from '@afterpaytouch/core'
import { useIsLDClientDefined, useWaitForLDClientInitialisation } from '@afterpaytouch/feature'
import React, { useEffect, useState } from 'react'
import { Cohort, Country } from '@afterpaytouch/portal-api/types'
import { Head } from '../../component'
import { Route, useCashAuthRoute } from '../../config/router'
import { AllowedQuery, CheckoutPage, InitialData, ConvergenceRollout, ConvergenceUnavailable, InitialLoadingRouteProps } from '../../model'
import { PageNames, TrackingEvent } from '../../model/monitoring'
import {
  setCheckoutError,
  useAppDispatch,
  useAppRouter,
  persistor,
  useOtpLoginRouteAndTrigger,
  useBiometricsEligibleAndRegisteredCheckTrigger,
  setAllowedQueryParam,
  useCheckoutState,
  useAllowedQuery,
  setConvergenceReadiness,
  useCheckoutTheme,
  useSetCashCohortLookupData,
} from '../../state'
import { initialise } from '../../utils/initialise'
import { initialiseZoid } from '../../utils/merchant'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useInitLog, useInitXbtBrowserRedirect, useMerchantCheckoutPreselectExperiment, useTrustedCheckout } from './hooks'
import { useTranslation } from '../../utils/convergence'
import { evaluateCohort } from '../../state/convergence/helper'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'

export interface IndexEntryProps {
  initialData: InitialData | null
  country: Country
  referrer?: string | null
  usernameFromCookie?: string
  allowedQueryParam: AllowedQuery
  rollout: ConvergenceRollout | undefined
  cohort?: Cohort | null
  ldKey: string
}

export const IndexEntryPage: CheckoutPage<IndexEntryProps> = ({
  initialData,
  country,
  referrer,
  usernameFromCookie,
  allowedQueryParam,
  rollout = ConvergenceUnavailable.Unavailable,
  cohort,
  ldKey,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { status } = useCheckoutState()
  const otpLoginRouteAndTrigger = useOtpLoginRouteAndTrigger()

  const biometricsEligibleAndRegisteredCheckTrigger = useBiometricsEligibleAndRegisteredCheckTrigger()
  const [initialised, setInitialised] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState('')
  const { skipPrefill } = useAllowedQuery()
  const [currentTheme] = useCheckoutTheme()
  const setCashCohortLookupData = useSetCashCohortLookupData()
  const cashAuthRoute = useCashAuthRoute()

  const isLDClientDefined = useIsLDClientDefined()
  const waitForLDToInitialise = useWaitForLDClientInitialisation()

  useMerchantCheckoutPreselectExperiment(initialData?.checkoutButtonLabel)

  useTrustedCheckout()

  useInitLog(status, initialised, usernameFromCookie)

  useInitXbtBrowserRedirect(redirectUrl)

  useEffect(() => {
    if (!isLDClientDefined) {
      return
    }

    router.prefetch(Route.ENTER_PASSWORD)
    router.prefetch(Route.TERMINAL_ERROR)
    router.prefetch(Route.ENTER_EMAIL)
    router.prefetch(Route.INITIAL_LOADING)

    const initZoid = async (): Promise<void> => {
      if (initialData?.protocol === 'zoid' && initialData.zoid) {
        try {
          await initialiseZoid(initialData.zoid)
          logEvent('Zoid Initialised Successfully')
        } catch (e) {
          logEvent('Zoid Initialisation Failure: ', {
            exception: e,
          })
        }
      }
    }

    const init = async (): Promise<void> => {
      if (initialised) {
        return
      }
      await initZoid()
      await waitForLDToInitialise()
      await dispatch(setConvergenceReadiness({ rollout, cohort, ldKey }))
      const result = await dispatch(
        initialise({
          initialData,
          country,
          otpLoginRouteAndTrigger,
          biometricsEligibleAndRegisteredCheckTrigger,
          referrer: referrer ?? null,
          usernameFromCookie: usernameFromCookie ?? null,
          skipPrefill,
          rollout,
          logEvent,
          cashAuthRoute,
        })
      )

      if (result === undefined) {
        router.replace(Route.TERMINAL_ERROR)
        return
      }

      dispatch(setAllowedQueryParam(allowedQueryParam))

      setInitialised(true)

      if ('lookup' in result) {
        const lookup = result?.lookup
        if (valueIsNotNullOrUndefined(lookup)) {
          const { cohort, theme } = evaluateCohort(
            lookup.cashtag,
            lookup.existing,
            rollout,
            lookup.email,
            lookup.shadowAccountExists,
            lookup.merchantId.toString(),
            country,
            lookup.merchantOrderSource
          )
          setCashCohortLookupData({
            cohort,
            cashEligibilitySignals: lookup.cashEligibilitySignals,
            cashCreationDate: lookup.cashCreationDate,
            shadowAccountExists: lookup.shadowAccountExists,
            cashLoginType: lookup.loginType,
          })

          logEvent(TrackingEvent.ACCOUNT_LOOKUP_ATTEMPTED, {
            customerCohort: cohort,
            checkoutBrand: theme,
            shadowAccountExists: lookup.shadowAccountExists,
            convergenceRollout: rollout,
            existing: lookup.existing,
            nextRoute: result?.route ?? undefined,
            lookupType: 'automated',
          })

          const isShadowAccount = String(lookup.shadowAccountExists ?? false)
          const isPersistentLogin = String(result?.persistentLogin ?? false)
          router.replaceQuery<InitialLoadingRouteProps>({
            pathname: Route.INITIAL_LOADING,
            query: { nextRoute: result.route, isShadowAccount, isPersistentLogin },
          })
          return
        }
      } else {
        logEvent(TrackingEvent.ACCOUNT_LOOKUP_ATTEMPTED, {
          customerCohort: cohort,
          checkoutBrand: currentTheme,
          convergenceRollout: rollout,
          hasLookup: false,
          lookupType: 'automated',
        })
      }

      if ('route' in result) {
        const isShadowAccount = String(result?.lookup?.shadowAccountExists ?? false)

        router.replaceQuery<InitialLoadingRouteProps>({
          pathname: Route.INITIAL_LOADING,
          query: { nextRoute: result.route, isShadowAccount },
        })
      } else if ('error' in result) {
        logEvent('Checkout Fatal Error', {
          errorCode: result.error,
          token: initialData?.token,
        })
        dispatch(setCheckoutError(result))
        router.replace(Route.TERMINAL_ERROR)
      } else if ('crossRegionCheckoutUrl' in result) {
        setRedirectUrl(result.crossRegionCheckoutUrl)
      }
    }

    persistor?.purge().then(init, init)
  }, [isLDClientDefined, rollout])

  return (
    <>
      <Head title={t('common:brandName')} />
      <Loader alignCenter />
      <TrackOnMount
        eventName={TrackingEvent.INDEX_PAGE_LOADED}
        eventProperties={{
          pageName: PageNames.INDEX,
          transactionToken: initialData?.token,
          protocol: initialData?.protocol,
          zoid: initialData?.zoid,
        }}
      />
    </>
  )
}
